'use client';

import { useState } from 'react';

interface Course {
  grade: string;
  credits: number;
}

const INITIAL_COURSES: Course[] = [
  { grade: 'A', credits: 3 },
  { grade: 'B', credits: 3 },
  { grade: 'A', credits: 4 },
];

const PRESETS = {
  conservative: [
    { grade: 'B', credits: 3 },
    { grade: 'B', credits: 3 },
    { grade: 'C+', credits: 3 },
  ],
  moderate: [
    { grade: 'A', credits: 3 },
    { grade: 'B', credits: 3 },
    { grade: 'A', credits: 4 },
  ],
  aggressive: [
    { grade: 'A+', credits: 4 },
    { grade: 'A', credits: 4 },
    { grade: 'A', credits: 3 },
  ],
};

export default function CalculatorGPA() {
  const [courses, setCourses] = useState<Course[]>(INITIAL_COURSES);
  const [isCalculating, setIsCalculating] = useState(false);
  const [showCustomScale, setShowCustomScale] = useState(false);

  const [gradePoints, setGradePoints] = useState<{ [key: string]: number }>({
    'A+': 4.0, 'A': 4.0, 'A-': 3.7,
    'B+': 3.3, 'B': 3.0, 'B-': 2.7,
    'C+': 2.3, 'C': 2.0, 'C-': 1.7,
    'D+': 1.3, 'D': 1.0, 'F': 0.0,
  });

  const handleChange = () => {
    setIsCalculating(true);
    setTimeout(() => setIsCalculating(false), 800);
  };

  const updateCourse = (index: number, field: keyof Course, value: string | number) => {
    const newCourses = [...courses];
    newCourses[index] = { ...newCourses[index], [field]: value };
    setCourses(newCourses);
    handleChange();
  };

  const updateGradePoint = (grade: string, value: number) => {
    setGradePoints({ ...gradePoints, [grade]: value });
    handleChange();
  };

  const addCourse = () => {
    setCourses([...courses, { grade: 'A', credits: 3 }]);
  };

  const removeCourse = (index: number) => {
    setCourses(courses.filter((_, i) => i !== index));
    handleChange();
  };

  const totalPoints = courses.reduce((sum, course) => sum + (gradePoints[course.grade] || 0) * course.credits, 0);
  const totalCredits = courses.reduce((sum, course) => sum + course.credits, 0);
  const gpa = totalCredits > 0 ? totalPoints / totalCredits : 0;

  const handleReset = () => {
    setCourses(INITIAL_COURSES);
  };

  const handlePreset = (preset: keyof typeof PRESETS) => {
    setCourses(PRESETS[preset]);
  };

  return (
    <div className="max-w-2xl mx-auto">
      <div className="bg-white rounded-2xl p-8 shadow-lg border border-slate-200">
        {isCalculating && (
          <div className="mb-6 bg-emerald-50 border border-emerald-200 rounded-lg p-4 text-center">
            <div className="flex items-center justify-center gap-2">
              <div className="w-5 h-5 border-3 border-emerald-500 border-t-transparent rounded-full animate-spin"></div>
              <span className="text-emerald-700 font-semibold">Calculating...</span>
            </div>
          </div>
        )}

        {/* Preset and Reset Buttons */}
        <div className="mb-6 space-y-3">
          <div className="grid grid-cols-3 gap-2">
            <button
              onClick={() => handlePreset('conservative')}
              className="px-3 py-2 bg-blue-50 hover:bg-blue-100 text-blue-700 rounded-lg font-semibold text-sm transition-all border border-blue-200"
            >
              Conservative
            </button>
            <button
              onClick={() => handlePreset('moderate')}
              className="px-3 py-2 bg-indigo-50 hover:bg-indigo-100 text-indigo-700 rounded-lg font-semibold text-sm transition-all border border-indigo-200"
            >
              Moderate
            </button>
            <button
              onClick={() => handlePreset('aggressive')}
              className="px-3 py-2 bg-purple-50 hover:bg-purple-100 text-purple-700 rounded-lg font-semibold text-sm transition-all border border-purple-200"
            >
              Aggressive
            </button>
          </div>
          <button
            onClick={handleReset}
            className="w-full px-4 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-lg font-semibold text-sm transition-all border border-slate-300"
          >
            Reset to Default
          </button>
        </div>

        <div className="mb-6">
          <button
            onClick={() => setShowCustomScale(!showCustomScale)}
            className="w-full py-3 px-4 bg-slate-100 hover:bg-slate-200 rounded-lg text-slate-700 font-semibold transition-all flex items-center justify-between"
          >
            <span>Customize Grade Point Scale</span>
            <span className="text-xl">{showCustomScale ? '−' : '+'}</span>
          </button>
          
          {showCustomScale && (
            <div className="mt-4 p-4 bg-slate-50 rounded-lg border-2 border-slate-200">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {Object.keys(gradePoints).map((grade) => (
                  <div key={grade}>
                    <label className="block text-xs font-semibold text-slate-600 mb-1">{grade}</label>
                    <input
                      type="number"
                      value={gradePoints[grade]}
                      onChange={(e) => updateGradePoint(grade, Number(e.target.value))}
                      min={0}
                      max={4.5}
                      step={0.1}
                      className="w-full px-2 py-2 border-2 border-slate-200 rounded-lg focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 outline-none transition-all font-semibold text-slate-900 text-sm"
                    />
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        <div className="space-y-4 mb-6">
          {courses.map((course, index) => (
            <div key={index} className="flex gap-3 items-center">
              <select
                value={course.grade}
                onChange={(e) => updateCourse(index, 'grade', e.target.value)}
                className="flex-1 px-3 py-3 border-2 border-slate-200 rounded-lg focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 outline-none transition-all font-semibold text-slate-900"
              >
                {Object.keys(gradePoints).map((grade) => (
                  <option key={grade} value={grade}>{grade} ({gradePoints[grade].toFixed(1)})</option>
                ))}
              </select>
              <input
                type="number"
                value={course.credits}
                onChange={(e) => updateCourse(index, 'credits', Number(e.target.value))}
                min={1}
                max={6}
                className="w-24 px-3 py-3 border-2 border-slate-200 rounded-lg focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 outline-none transition-all font-semibold text-slate-900"
              />
              <span className="text-sm text-slate-600 w-16">credits</span>
              {courses.length > 1 && (
                <button
                  onClick={() => removeCourse(index)}
                  className="text-red-500 hover:text-red-700 font-bold"
                >
                  ✕
                </button>
              )}
            </div>
          ))}
        </div>

        <button
          onClick={addCourse}
          className="w-full py-3 border-2 border-dashed border-slate-300 rounded-lg text-slate-600 hover:border-emerald-500 hover:text-emerald-600 font-semibold transition-all mb-8"
        >
          + Add Course
        </button>

        <div className="bg-gradient-to-br from-yellow-500 via-amber-500 to-orange-600 rounded-xl p-6 text-white text-center">
          <div className="text-sm font-semibold uppercase tracking-wider opacity-90 mb-2">Your GPA</div>
          <div className="text-5xl font-bold">{gpa.toFixed(2)}</div>
          <div className="text-sm opacity-90 mt-2">{totalCredits} total credits</div>
        </div>
      </div>
    </div>
  );
}